import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import type { LiveWineView } from '@gwc/shared';
export function TastingSummary({ eventId }: { eventId: string }) {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ['summary', eventId],
    queryFn: () =>
      api.get<{
        title: string;
        results: LiveWineView[];
        photos: { url: string; caption: string | null }[];
        memberCount: number;
      }>(`/summaries/${eventId}`),
  });
  const send = useMutation({
    mutationFn: () => api.post<{ alreadySent: boolean }>(`/summaries/${eventId}/send`, {}),
  });
  if (q.isLoading) return <p>Loading summary…</p>;
  if (!q.data) return <p>{q.error?.message}</p>;
  return (
    <section className="card space-y-4">
      <h2 className="font-display text-2xl">Event summary · Your palate vs. the group</h2>
      {q.data.results.map((w) => (
        <div key={w.id}>
          <strong>
            #{w.groupRank ?? '—'} · {w.letter} · {w.wineName}
          </strong>
          <p>
            Your score {w.myScore?.total ?? '—'} · Group average {w.groupAverage?.toFixed(2) ?? '—'}{' '}
            · Group total {w.groupScore ?? '—'}
          </p>
          {w.myScore?.notes && <p>Your notes: {w.myScore.notes}</p>}
        </div>
      ))}
      <div className="flex flex-wrap gap-3">
        {q.data.photos.map((p) => (
          <img
            key={p.url}
            className="h-40 rounded object-contain"
            src={p.url}
            alt={p.caption ?? 'Event photo'}
          />
        ))}
      </div>
      {user?.roles.includes('site_admin') && (
        <div>
          <p>
            This summary’s wine results and photos will be shared with the full membership (
            {q.data.memberCount} active members). Personal scores and notes stay in each member’s
            own view.
          </p>
          <button
            className="btn-primary mt-2"
            disabled={send.isPending || send.isSuccess}
            onClick={() => send.mutate()}
          >
            Send summary to full membership
          </button>
        </div>
      )}
      {send.isSuccess && (
        <p role="status">
          {send.data.alreadySent
            ? 'This summary was already sent.'
            : 'Summary queued for delivery.'}
        </p>
      )}
      {send.error && <p role="alert">{send.error.message}</p>}
    </section>
  );
}
